import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CoachTournamentsAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Minus,
  Save,
  Trash2,
  ChevronLeft,
  Trophy,
  Crown,
  Pencil,
  X,
  Link2,
  Copy,
  RefreshCw,
} from "lucide-react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { SPORT_IDS, getSport, effectiveScoring, computePoints } from "@/lib/sports";

/**
 * CoachTournaments — embedded under CoachDashboard "Tournois" tab.
 * Two zones:
 *  - Tournaments organized by the coach (full edit + delete)
 *  - Tournaments where coach has a team (own row edit only)
 * Coaches can also create new tournaments (they become organizer).
 */
export default function CoachTournaments({ user }) {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState({ mode: "list" }); // {mode:'list'|'edit'|'create', tournament?}

  const clubs = user?.assigned_clubs || [];

  const load = async () => {
    setLoading(true);
    try {
      const list = await CoachTournamentsAPI.list();
      setTournaments(list);
    } catch {
      toast.error("Impossible de charger les tournois");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const { mine, participating } = useMemo(() => {
    const mine = [];
    const participating = [];
    for (const t of tournaments) {
      if (t.organizer_email && t.organizer_email === user?.email) mine.push(t);
      else participating.push(t);
    }
    return { mine, participating };
  }, [tournaments, user]);

  if (view.mode === "create") {
    return (
      <CoachTournamentForm
        clubs={clubs}
        onCancel={() => setView({ mode: "list" })}
        onSaved={() => {
          setView({ mode: "list" });
          load();
        }}
      />
    );
  }
  if (view.mode === "edit") {
    return (
      <CoachTournamentEditor
        tournament={view.tournament}
        clubs={clubs}
        userEmail={user?.email}
        onBack={() => setView({ mode: "list" })}
        onChanged={(next) => {
          setTournaments((arr) => arr.map((t) => (t.id === next.id ? next : t)));
          setView({ mode: "edit", tournament: next });
        }}
        onDeleted={() => {
          setView({ mode: "list" });
          load();
        }}
      />
    );
  }

  return (
    <div data-testid="coach-tournaments">
      <div className="flex items-center justify-between gap-3 mb-5">
        <p className="text-zinc-500 text-sm font-body">
          Gère les tournois que tu organises ou édite la ligne de ton équipe
          dans les tournois où tu participes.
        </p>
        <Button
          onClick={() => setView({ mode: "create" })}
          data-testid="coach-new-tournament-btn"
          className="text-black font-bold uppercase tracking-[0.18em] text-xs h-9 px-4 shrink-0"
          style={{
            background:
              "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
          }}
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Nouveau tournoi
        </Button>
      </div>

      {loading ? (
        <div className="text-center text-zinc-500 py-12 font-body">
          Chargement…
        </div>
      ) : (
        <>
          <Section
            title="Mes tournois (organisateur)"
            icon={<Crown className="w-3.5 h-3.5 text-[#D4AF37]" />}
            items={mine}
            empty="Tu n'organises aucun tournoi pour le moment."
            onPick={(t) => setView({ mode: "edit", tournament: t })}
            isOrganizer
          />
          <Section
            title="Tournois où je participe"
            icon={<Trophy className="w-3.5 h-3.5 text-zinc-400" />}
            items={participating}
            empty="Ton équipe ne participe à aucun tournoi pour l'instant."
            onPick={(t) => setView({ mode: "edit", tournament: t })}
            clubs={clubs}
          />
        </>
      )}
    </div>
  );
}

function Section({ title, icon, items, empty, onPick, isOrganizer, clubs }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h2 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-zinc-300">
          {title}
        </h2>
        <span className="text-[10px] tabular-nums px-1.5 py-0.5 rounded bg-white/10 text-zinc-300 font-bold">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="metal-card rounded-xl p-6 text-center text-zinc-500 font-body text-sm">
          {empty}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {items.map((t) => {
            const myTeam = clubs
              ? (t.teams || []).find((tm) =>
                  clubs
                    .map((c) => c.toLowerCase())
                    .includes((tm.name || "").trim().toLowerCase()),
                )
              : null;
            return (
              <button
                key={t.id}
                onClick={() => onPick(t)}
                data-testid={`coach-tournament-${t.slug}`}
                className="metal-card rounded-xl p-4 text-left flex items-center gap-3 hover:border-white/30 transition-all"
              >
                <div
                  className="w-12 h-12 rounded-lg overflow-hidden border shrink-0"
                  style={{
                    borderColor: `${t.primary_color || "#D4AF37"}66`,
                    background: `${t.primary_color || "#D4AF37"}1A`,
                  }}
                >
                  {t.cover_image_url && (
                    <img
                      src={t.cover_image_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold uppercase tracking-tight truncate">
                    {t.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 truncate">
                    {[t.sport, t.season].filter(Boolean).join(" · ")}
                  </div>
                  {myTeam && (
                    <div className="text-[10px] text-zinc-400 mt-1 truncate">
                      Mon équipe :{" "}
                      <span className="text-white">{myTeam.name}</span>
                    </div>
                  )}
                </div>
                {isOrganizer ? (
                  <span className="text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] bg-[#D4AF37]/10 shrink-0">
                    Organisateur
                  </span>
                ) : (
                  <Pencil className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------- Create form (lightweight) ---------- */
function CoachTournamentForm({ clubs, onCancel, onSaved }) {
  const [form, setForm] = useState({
    name: "",
    sport: "football",
    season: "",
    subtitle: "",
    cover_image_url: "",
    teams: clubs.length > 0 ? [{ name: clubs[0] }] : [{ name: "" }],
  });
  const [saving, setSaving] = useState(false);

  const setTeam = (i, val) =>
    setForm((f) => ({
      ...f,
      teams: f.teams.map((t, idx) => (idx === i ? { ...t, name: val } : t)),
    }));
  const addTeam = () =>
    setForm((f) => ({ ...f, teams: [...f.teams, { name: "" }] }));
  const removeTeam = (i) =>
    setForm((f) => ({ ...f, teams: f.teams.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Donne un nom au tournoi");
      return;
    }
    const teams = form.teams.filter((t) => t.name.trim()).map((t) => ({
      name: t.name.trim(),
    }));
    if (teams.length < 2) {
      toast.error("Il faut au moins 2 équipes");
      return;
    }
    setSaving(true);
    try {
      await CoachTournamentsAPI.create({
        name: form.name.trim(),
        sport: form.sport,
        season: form.season,
        subtitle: form.subtitle,
        cover_image_url: form.cover_image_url,
        teams,
      });
      toast.success("Tournoi créé. Tu en es l'organisateur.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la création");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={submit}
      className="metal-card rounded-xl p-5"
      data-testid="coach-tournament-create-form"
    >
      <button
        type="button"
        onClick={onCancel}
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white mb-5"
      >
        <ChevronLeft className="w-3 h-3" /> Retour
      </button>

      <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-1">
        Nouveau tournoi
      </h2>
      <p className="text-zinc-400 text-sm font-body mb-6">
        Tu deviens l'organisateur : tu pourras tout éditer (équipes, classement,
        infos).
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div>
          <Label className="text-overline">Nom</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Coupe de Genève U17"
            className="bg-white/[0.04] border-white/10 text-white"
            data-testid="ct-name"
            required
          />
        </div>
        <div>
          <Label className="text-overline">Sport</Label>
          <Select
            value={form.sport}
            onValueChange={(v) => setForm({ ...form, sport: v })}
          >
            <SelectTrigger
              data-testid="ct-sport"
              className="bg-white/[0.04] border-white/10 text-white"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SPORT_IDS.map((id) => (
                <SelectItem key={id} value={id}>
                  {getSport(id).label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-overline">Saison</Label>
          <Input
            value={form.season}
            onChange={(e) => setForm({ ...form, season: e.target.value })}
            placeholder="2025/2026"
            className="bg-white/[0.04] border-white/10 text-white"
            data-testid="ct-season"
          />
        </div>
        <div>
          <Label className="text-overline">Image couverture (URL)</Label>
          <Input
            value={form.cover_image_url}
            onChange={(e) =>
              setForm({ ...form, cover_image_url: e.target.value })
            }
            placeholder="https://…"
            className="bg-white/[0.04] border-white/10 text-white"
            data-testid="ct-cover"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-overline">Sous-titre</Label>
          <Input
            value={form.subtitle}
            onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
            placeholder="Phase de groupes, Demi-finales…"
            className="bg-white/[0.04] border-white/10 text-white"
            data-testid="ct-subtitle"
          />
        </div>
      </div>

      <Label className="text-overline mb-2 block">Équipes participantes</Label>
      <div className="space-y-2 mb-5">
        {form.teams.map((t, i) => (
          <div key={i} className="flex gap-2" data-testid={`ct-team-${i}`}>
            <Input
              value={t.name}
              onChange={(e) => setTeam(i, e.target.value)}
              placeholder={`Équipe ${i + 1}`}
              list="coach-clubs"
              className="bg-white/[0.04] border-white/10 text-white flex-1"
            />
            {form.teams.length > 2 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => removeTeam(i)}
                className="border-white/15 text-zinc-400 hover:text-white h-9 w-9 p-0"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ))}
        <datalist id="coach-clubs">
          {clubs.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addTeam}
          className="border-white/15 text-zinc-300 hover:text-white"
          data-testid="ct-add-team"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Ajouter une équipe
        </Button>
      </div>

      <div className="flex gap-3">
        <Button
          type="submit"
          disabled={saving}
          data-testid="ct-submit"
          className="text-black font-bold uppercase tracking-[0.18em] text-xs h-10 px-6"
          style={{
            background:
              "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
          }}
        >
          <Save className="w-3.5 h-3.5 mr-2" />
          {saving ? "Création…" : "Créer le tournoi"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          className="text-zinc-400 hover:text-white"
        >
          Annuler
        </Button>
      </div>
    </motion.form>
  );
}

/* ---------- Editor: full edit if organizer, row edit otherwise ---------- */
function CoachTournamentEditor({
  tournament,
  clubs,
  userEmail,
  onBack,
  onChanged,
  onDeleted,
}) {
  const isOrganizer = tournament.organizer_email === userEmail;
  const sportDef = getSport(tournament.sport || "football");
  const scoring = effectiveScoring(tournament.sport || "football", tournament.scoring);
  const clubsLower = clubs.map((c) => c.toLowerCase());

  const ownedTeamIds = (tournament.teams || [])
    .filter((t) => clubsLower.includes((t.name || "").trim().toLowerCase()))
    .map((t) => t.id);

  // Editable team rows: clone and track per-team pending changes
  const [rows, setRows] = useState(() =>
    (tournament.teams || []).map((t) => {
      const auto = computePoints(t, scoring);
      return {
        ...t,
        points_manual: t.points_manual || (auto !== null && auto !== (t.points || 0)),
      };
    }),
  );
  const [savingTeamId, setSavingTeamId] = useState(null);
  const [meta, setMeta] = useState({
    name: tournament.name,
    season: tournament.season || "",
    subtitle: tournament.subtitle || "",
    cover_image_url: tournament.cover_image_url || "",
  });
  const [tournamentStats, setTournamentStats] = useState(
    tournament.stats || {},
  );
  const [savingMeta, setSavingMeta] = useState(false);
  const [savingStats, setSavingStats] = useState(false);
  const [inviteCode, setInviteCode] = useState(tournament.invite_code || null);
  const [inviteBusy, setInviteBusy] = useState(false);

  const inviteUrl = inviteCode
    ? `${window.location.origin}/tournament/join/${inviteCode}`
    : "";

  const generateInvite = async () => {
    setInviteBusy(true);
    try {
      const updated = await CoachTournamentsAPI.generateInvite(tournament.id);
      setInviteCode(updated.invite_code);
      onChanged(updated);
      toast.success("Lien d'invitation généré");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la génération");
    } finally {
      setInviteBusy(false);
    }
  };

  const revokeInvite = async () => {
    if (!window.confirm("Désactiver le lien actuel ? Personne ne pourra plus rejoindre."))
      return;
    setInviteBusy(true);
    try {
      const updated = await CoachTournamentsAPI.revokeInvite(tournament.id);
      setInviteCode(null);
      onChanged(updated);
      toast.success("Lien désactivé");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la révocation");
    } finally {
      setInviteBusy(false);
    }
  };

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Lien copié");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const downloadQR = () => {
    const canvas = document.querySelector(
      `[data-testid="ct-invite-qr"] canvas`,
    );
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `tournoi-${tournament.slug}-invite.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const editableRow = (team) => isOrganizer || ownedTeamIds.includes(team.id);

  const applyRowChange = (t, key, value) => {
    const next = { ...t, [key]: value };
    if (!scoring) return next;
    if (key === "points") return { ...next, points_manual: true };
    if (key in scoring && !next.points_manual)
      return { ...next, points: computePoints(next, scoring) };
    return next;
  };

  const setRowField = (id, key, value) => {
    setRows((arr) =>
      arr.map((t) =>
        t.id === id
          ? applyRowChange(
              t,
              key,
              key === "extra" ? value : Math.max(0, parseInt(value || 0, 10) || 0),
            )
          : t,
      ),
    );
  };

  const inc = (id, key, delta) => {
    setRows((arr) =>
      arr.map((t) =>
        t.id === id
          ? applyRowChange(t, key, Math.max(0, (parseInt(t[key], 10) || 0) + delta))
          : t,
      ),
    );
  };

  const saveTeamRow = async (team) => {
    setSavingTeamId(team.id);
    try {
      const payload = { team_id: team.id };
      for (const col of sportDef.standings_columns) {
        payload[col.key] = parseInt(team[col.key], 10) || 0;
      }
      payload.points_manual = !!team.points_manual;
      const updated = await CoachTournamentsAPI.updateTeam(
        tournament.id,
        payload,
      );
      toast.success(`Stats enregistrées pour ${team.name}`);
      setRows(updated.teams || []);
      onChanged(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSavingTeamId(null);
    }
  };

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      const updated = await CoachTournamentsAPI.update(tournament.id, meta);
      toast.success("Tournoi mis à jour");
      onChanged(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSavingMeta(false);
    }
  };

  const incStat = (key, delta) => {
    setTournamentStats((s) => ({
      ...s,
      [key]: Math.max(0, (parseInt(s[key], 10) || 0) + delta),
    }));
  };

  const setStat = (key, value) => {
    setTournamentStats((s) => ({
      ...s,
      [key]: Math.max(0, parseInt(value || 0, 10) || 0),
    }));
  };

  const saveStats = async () => {
    setSavingStats(true);
    try {
      const updated = await CoachTournamentsAPI.update(tournament.id, {
        stats: tournamentStats,
      });
      toast.success("Stats du tournoi mises à jour");
      onChanged(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSavingStats(false);
    }
  };

  const removeTournament = async () => {
    if (!window.confirm(`Supprimer définitivement « ${tournament.name} » ?`))
      return;
    try {
      await CoachTournamentsAPI.remove(tournament.id);
      toast.success("Tournoi supprimé");
      onDeleted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la suppression");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="coach-tournament-editor"
    >
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white mb-5"
        data-testid="coach-tournament-back"
      >
        <ChevronLeft className="w-3 h-3" /> Tous les tournois
      </button>

      <div className="metal-card rounded-xl p-5 mb-5 flex items-start gap-4">
        <div
          className="w-16 h-16 rounded-lg overflow-hidden border shrink-0"
          style={{
            borderColor: `${tournament.primary_color || "#D4AF37"}66`,
            background: `${tournament.primary_color || "#D4AF37"}1A`,
          }}
        >
          {tournament.cover_image_url && (
            <img
              src={tournament.cover_image_url}
              alt=""
              className="w-full h-full object-cover"
            />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl sm:text-2xl font-black uppercase tracking-tight">
            {tournament.name}
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
            {[tournament.sport, tournament.season].filter(Boolean).join(" · ")}
          </div>
          {isOrganizer && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] bg-[#D4AF37]/10">
              <Crown className="w-3 h-3" /> Organisateur
            </div>
          )}
        </div>
        <Link
          to={`/tournament/${tournament.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 hover:text-white border border-white/10 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 shrink-0"
        >
          Voir page
        </Link>
      </div>

      {isOrganizer && (
        <div className="metal-card rounded-xl p-5 mb-5">
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-zinc-300 mb-4">
            Infos du tournoi
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div>
              <Label className="text-overline">Nom</Label>
              <Input
                value={meta.name}
                onChange={(e) => setMeta({ ...meta, name: e.target.value })}
                className="bg-white/[0.04] border-white/10 text-white"
                data-testid="ct-meta-name"
              />
            </div>
            <div>
              <Label className="text-overline">Saison</Label>
              <Input
                value={meta.season}
                onChange={(e) => setMeta({ ...meta, season: e.target.value })}
                className="bg-white/[0.04] border-white/10 text-white"
                data-testid="ct-meta-season"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-overline">Sous-titre</Label>
              <Input
                value={meta.subtitle}
                onChange={(e) =>
                  setMeta({ ...meta, subtitle: e.target.value })
                }
                className="bg-white/[0.04] border-white/10 text-white"
                data-testid="ct-meta-subtitle"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-overline">Image couverture (URL)</Label>
              <Input
                value={meta.cover_image_url}
                onChange={(e) =>
                  setMeta({ ...meta, cover_image_url: e.target.value })
                }
                className="bg-white/[0.04] border-white/10 text-white"
                data-testid="ct-meta-cover"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={saveMeta}
              disabled={savingMeta}
              data-testid="ct-meta-save"
              className="text-black font-bold uppercase tracking-[0.18em] text-xs h-10 px-5"
              style={{
                background:
                  "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
              }}
            >
              <Save className="w-3.5 h-3.5 mr-2" />
              {savingMeta ? "Envoi…" : "Enregistrer les infos"}
            </Button>
            <Button
              onClick={removeTournament}
              variant="outline"
              data-testid="ct-delete"
              className="border-red-500/40 bg-red-500/5 text-red-400 hover:bg-red-500/15"
            >
              <Trash2 className="w-3.5 h-3.5 mr-2" /> Supprimer le tournoi
            </Button>
          </div>
        </div>
      )}

      {isOrganizer && sportDef.tournament_stats?.length > 0 && (
        <div
          className="metal-card rounded-xl p-5 mb-5"
          data-testid="ct-stats-block"
        >
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-zinc-300 mb-4">
            Stats du tournoi
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {sportDef.tournament_stats.map((field) => (
              <div
                key={field.key}
                className="bg-white/[0.04] border border-white/10 rounded-lg p-3"
                data-testid={`ct-stat-${field.key}`}
              >
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-body">
                  {field.label}
                </div>
                <div className="flex items-center gap-1.5 mt-2">
                  <button
                    type="button"
                    onClick={() => incStat(field.key, -1)}
                    data-testid={`ct-stat-${field.key}-minus`}
                    className="w-7 h-7 rounded-full flex items-center justify-center border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-zinc-300"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <input
                    type="number"
                    value={tournamentStats[field.key] ?? 0}
                    onChange={(e) => setStat(field.key, e.target.value)}
                    className="w-full bg-transparent text-center text-lg font-bold tabular-nums text-white outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => incStat(field.key, 1)}
                    data-testid={`ct-stat-${field.key}-plus`}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-black font-bold"
                    style={{
                      background:
                        "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
                    }}
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <Button
            onClick={saveStats}
            disabled={savingStats}
            data-testid="ct-stats-save"
            className="text-black font-bold uppercase tracking-[0.18em] text-xs h-10 px-5"
            style={{
              background:
                "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
            }}
          >
            <Save className="w-3.5 h-3.5 mr-2" />
            {savingStats ? "Envoi…" : "Enregistrer les stats"}
          </Button>
        </div>
      )}

      {isOrganizer && (
        <div
          className="metal-card rounded-xl p-5 mb-5"
          data-testid="ct-invite-block"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-[#D4AF37]/10 border border-[#D4AF37]/30">
              <Link2 className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-zinc-200">
                Lien d'invitation
              </h3>
              <p className="text-zinc-400 font-body text-xs mt-1">
                Partage ce QR/lien avec les autres clubs : ils s'inscrivent
                directement dans ton tournoi sans toucher à ton compte.
              </p>
            </div>
          </div>

          {!inviteCode ? (
            <Button
              onClick={generateInvite}
              disabled={inviteBusy}
              data-testid="ct-invite-generate"
              className="text-black font-bold uppercase tracking-[0.18em] text-xs h-10 px-5"
              style={{
                background:
                  "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
              }}
            >
              <Link2 className="w-3.5 h-3.5 mr-2" />
              {inviteBusy ? "Génération…" : "Générer un lien d'invitation"}
            </Button>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-5 items-start">
              <div
                className="bg-white p-3 rounded-lg w-fit"
                data-testid="ct-invite-qr"
              >
                <QRCodeCanvas
                  value={inviteUrl}
                  size={160}
                  level="M"
                  includeMargin={false}
                />
              </div>
              <div className="min-w-0 space-y-3">
                <div>
                  <Label className="text-overline">Code</Label>
                  <div
                    className="font-display text-2xl font-black tracking-[0.2em] tabular-nums"
                    data-testid="ct-invite-code"
                  >
                    {inviteCode}
                  </div>
                </div>
                <div>
                  <Label className="text-overline">URL publique</Label>
                  <div className="flex gap-2 items-center">
                    <input
                      readOnly
                      value={inviteUrl}
                      data-testid="ct-invite-url"
                      onFocus={(e) => e.target.select()}
                      className="flex-1 bg-white/[0.04] border border-white/10 text-zinc-300 text-xs px-3 py-2 rounded font-mono truncate outline-none"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={copyInvite}
                      data-testid="ct-invite-copy"
                      className="border-white/15 bg-white/[0.04] text-zinc-300 hover:text-white"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={downloadQR}
                    data-testid="ct-invite-download"
                    className="border-white/15 bg-white/[0.04] text-zinc-300 hover:text-white"
                  >
                    Télécharger le QR
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={generateInvite}
                    disabled={inviteBusy}
                    data-testid="ct-invite-regenerate"
                    className="border-white/15 bg-white/[0.04] text-zinc-300 hover:text-white"
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Régénérer
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={revokeInvite}
                    disabled={inviteBusy}
                    data-testid="ct-invite-revoke"
                    className="border-red-500/40 bg-red-500/5 text-red-400 hover:bg-red-500/15"
                  >
                    Désactiver
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-zinc-300 mb-3">
        Classement{" "}
        {!isOrganizer && (
          <span className="text-[10px] text-zinc-500 normal-case font-body">
            (tu peux éditer uniquement la ligne de ton équipe)
          </span>
        )}
      </h3>

      <div className="space-y-3">
        {rows.map((team) => {
          const editable = editableRow(team);
          const owned = ownedTeamIds.includes(team.id);
          return (
            <div
              key={team.id}
              className="metal-card rounded-xl p-4"
              data-testid={`ct-row-${team.id}`}
              style={{
                opacity: editable ? 1 : 0.55,
                borderColor: owned ? "#D4AF37" : undefined,
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="font-display font-bold uppercase tracking-tight truncate">
                  {team.name}
                </div>
                {owned && (
                  <span className="text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] bg-[#D4AF37]/10">
                    Mon équipe
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mb-3">
                {sportDef.standings_columns.map((col) => (
                  <div
                    key={col.key}
                    className="bg-white/[0.04] border border-white/10 rounded-lg p-2"
                  >
                    <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500">
                      {col.label}
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <button
                        type="button"
                        disabled={!editable}
                        onClick={() => inc(team.id, col.key, -1)}
                        className="w-6 h-6 rounded-full bg-white/[0.06] border border-white/10 text-zinc-300 hover:bg-white/[0.12] disabled:opacity-30 flex items-center justify-center"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <input
                        type="number"
                        value={team[col.key] ?? 0}
                        disabled={!editable}
                        onChange={(e) =>
                          setRowField(team.id, col.key, e.target.value)
                        }
                        className="w-full bg-transparent text-center text-base font-bold tabular-nums text-white outline-none disabled:text-zinc-500"
                        data-testid={`ct-row-${team.id}-${col.key}`}
                      />
                      <button
                        type="button"
                        disabled={!editable}
                        onClick={() => inc(team.id, col.key, 1)}
                        className="w-6 h-6 rounded-full text-black font-bold flex items-center justify-center disabled:opacity-30"
                        style={{
                          background:
                            "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
                        }}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    {col.key === "points" && scoring && team.points_manual && editable && (
                      <button
                        type="button"
                        onClick={() =>
                          setRows((arr) =>
                            arr.map((t) =>
                              t.id === team.id
                                ? { ...t, points_manual: false, points: computePoints(t, scoring) }
                                : t,
                            ),
                          )
                        }
                        className="mt-1 text-[9px] uppercase tracking-[0.14em] text-[#D4AF37] hover:underline"
                        data-testid={`ct-points-auto-${team.id}`}
                      >
                        Auto
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {editable && (
                <Button
                  size="sm"
                  onClick={() => saveTeamRow(team)}
                  disabled={savingTeamId === team.id}
                  data-testid={`ct-row-save-${team.id}`}
                  className="text-black font-bold uppercase tracking-[0.18em] text-[10px] h-8 px-4"
                  style={{
                    background:
                      "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
                  }}
                >
                  <Save className="w-3 h-3 mr-1.5" />
                  {savingTeamId === team.id ? "Envoi…" : "Enregistrer"}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
